/**
 * API para relatórios de Ordens de Serviço
 * 
 * @author Equipe GestaoeCRM
 * @date 02/12/2025
 */

import api from './api';

export interface OSComment {
  id: string;
  comment: string;
  createdAt: string;
  updatedAt: string;
  formattedDate: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

export interface OSReportItem {
  id: string;
  title: string;
  description?: string;
  type: string;
  status: string;
  priority: string;
  startDate?: string;
  formattedStartDate?: string;
  actualHours: number;
  formattedHours: string;
  companyArea?: {
    id: string;
    name: string;
    description?: string;
  } | null;
  demand?: {
    id: string;
    title: string;
    status: string;
    priority: string;
  } | null;
  project?: {
    id: string;
    name: string;
    status: string;
  } | null;
  user: {
    id: string;
    name: string;
    email: string;
  };
  comments: OSComment[];
  commentsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface OSByAreaReport {
  report: Array<{
    area: {
      id: string | null;
      name: string;
      description?: string | null;
    };
    ordersOfService: OSReportItem[];
  }>;
  summary: {
    totalOS: number;
    totalHours: number;
    formattedTotalHours: string;
    totalComments: number;
    areasCount: number;
  };
  filters: {
    companyAreaId: string;
    userId: string;
  };
}

export interface OSCompletedReportItem {
  id: string;
  title: string;
  description?: string;
  type: string;
  priority: string;
  startDate?: string;
  endDate?: string;
  formattedStartDate?: string;
  formattedEndDate?: string;
  actualHours: number;
  formattedHours: string;
  estimatedHours?: number;
  formattedEstimatedHours?: string | null;
  companyArea?: {
    id: string;
    name: string;
    description?: string;
  } | null;
  demand?: {
    id: string;
    title: string;
    status: string;
    priority: string;
  } | null;
  project?: {
    id: string;
    name: string;
    status: string;
  } | null;
  user: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface OSCompletedReport {
  report: OSCompletedReportItem[];
  summary: {
    totalOS: number;
    totalHours: number;
    formattedTotalHours: string;
    totalEstimatedHours?: number | null;
    formattedTotalEstimatedHours?: string | null;
    averageHours: number;
    formattedAverageHours: string;
  };
}

/**
 * Busca relatório de OS por área
 * @param companyAreaId ID da área (opcional, 'all' para todas as áreas)
 * @param userId ID do usuário responsável (opcional, 'all' para todos os usuários)
 */
export const getOSByAreaReport = async (companyAreaId?: string, userId?: string): Promise<OSByAreaReport> => {
  const params: any = {};
  if (companyAreaId && companyAreaId !== 'all') {
    params.companyAreaId = companyAreaId;
  }
  if (userId && userId !== 'all') {
    params.userId = userId;
  }
  const response = await api.get('/reports/os-by-area', { params });
  return response.data.data;
};

/**
 * Busca relatório de OS concluídas
 */
export const getOSCompletedReport = async (): Promise<OSCompletedReport> => {
  const response = await api.get('/reports/os-completed');
  return response.data.data;
};

/**
 * Busca usuários que têm OS em uma área específica
 * @param companyAreaId ID da área
 */
export const getUsersByArea = async (companyAreaId: string): Promise<Array<{
  id: string;
  name: string;
  email: string;
}>> => {
  const response = await api.get('/reports/users-by-area', {
    params: { companyAreaId }
  });
  return response.data.data;
};

export const osReportsApi = {
  getOSByAreaReport,
  getOSCompletedReport,
  getUsersByArea
};

